'use client';
import React from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { CheckCircle, Search, Eye, CreditCard, UserCheck, ArrowRight } from 'lucide-react';

const benefits = [
  { title: 'Reduced Recruitment Effort', desc: 'Stop sorting hundreds of unscreened CVs. Every candidate is pre-assessed.', span: 'col-span-2 md:col-span-2', bg: 'var(--charcoal)', color: 'white' },
  { title: 'Better Quality Candidates', desc: 'Access candidates assessed on Integrity, Energy, Learning Ability, Humility and Practical Readiness.', span: 'col-span-2 md:col-span-1', bg: 'var(--amber)', color: 'var(--charcoal)' },
  { title: 'ID Verified', desc: 'Every candidate has completed South African ID verification before their profile is published.', span: 'col-span-2 md:col-span-1', bg: 'var(--off-white)', color: 'var(--charcoal)' },
  { title: 'Optional Police Clearance', desc: 'Add a criminal background check for roles requiring higher levels of trust.', span: 'col-span-2 md:col-span-1', bg: 'var(--off-white)', color: 'var(--charcoal)' },
  { title: 'Faster Hiring', desc: 'Structured interview notes and assessment summaries mean you spend less time in first-round interviews.', span: 'col-span-2 md:col-span-1', bg: 'rgba(43,41,38,0.85)', color: 'white' },
];

const processSteps = [
  { icon: Search, title: 'Search the Directory', desc: 'Filter by province, skills, job type, availability and work-readiness score.' },
  { icon: Eye, title: 'Review Profiles', desc: 'See anonymised candidate summaries, skills and assessment scores before committing.' },
  { icon: CreditCard, title: 'Purchase Access — R1,000', desc: 'Unlock the full profile, CV, contact details and interview notes.' },
  { icon: UserCheck, title: 'Interview & Hire', desc: 'Contact the candidate directly. Run your own process. We follow up at 30 and 90 days.' },
];

const pricingCards = [
  {
    title: 'Candidate Profile Unlock',
    price: 'R1,000',
    desc: 'Full access to a single candidate profile including CV, contact details and assessment summary.',
    features: ['Full CV access', 'Contact details', 'Interview notes', 'ID & reference verified', 'Work-Readiness Score', 'Assessment summary'],
    cta: 'Browse Candidates',
    href: '/directory',
    highlight: true,
  },
  {
    title: 'Police Clearance Add-on',
    price: 'Additional fee',
    desc: 'Optional criminal background check for roles requiring higher levels of trust.',
    features: ['Full criminal record check', 'Results within 3–5 days', 'Issued by accredited provider', 'Attached to candidate profile'],
    cta: 'Request with profile',
    href: '/directory',
    highlight: false,
  },
  {
    title: 'Employer Assisted Assessment',
    price: 'Custom',
    desc: 'Handlenders conducts a full Work-Readiness Interview, Verification Checks, Reference Checks and Assessment Report on your behalf.',
    features: ['Work-Readiness Interview', 'Verification checks', 'Reference checks', 'Full assessment report', 'Dedicated account support'],
    cta: 'Request Candidate Evaluation',
    href: '/sign-up-login',
    highlight: false,
  },
];

export default function EmployerPage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      {/* Hero */}
      <section className="relative overflow-hidden py-16 lg:py-24" style={{ background: 'var(--charcoal)' }}>
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{ background: 'var(--amber)', transform: 'translate(40%, -40%)' }} />
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-white/50">For Employers</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: 'var(--font-serif)' }}>
              Hire Verified, Work-Ready Talent.
            </h1>
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              Stop wading through hundreds of CVs. Access pre-screened, assessed candidates who have been verified for identity, references and work readiness.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/directory" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-150 active:scale-95" style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}>
                Browse the Directory
              </Link>
              <a href="#pricing" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base border-2 transition-all duration-150" style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
                See Pricing
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Problem / Solution */}
      <section className="py-16 lg:py-20" style={{ background: 'var(--off-white)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl p-8 lg:p-10" style={{ background: 'var(--off-white)', border: '1px solid rgba(43,41,38,0.12)' }}>
              <div className="text-3xl mb-4">😓</div>
              <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>The Problem</h3>
              <p className="text-muted-foreground leading-relaxed">
                Employers receive hundreds of CVs but have little confidence in candidate quality. Screening takes time you don&apos;t have — and even after interviews, you still can&apos;t be sure someone will show up with the right attitude.
              </p>
            </div>
            <div className="rounded-2xl p-8 lg:p-10" style={{ background: 'var(--success-bg)', border: '1px solid rgba(43,41,38,0.15)' }}>
              <div className="text-3xl mb-4">✅</div>
              <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>The Handlenders Solution</h3>
              <p className="text-muted-foreground leading-relaxed">
                Handlenders pre-screens and verifies candidates using a structured Work-Readiness Framework. You get access to a shortlist of assessed, ID-verified candidates — not a pile of unscreened CVs.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Bento */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--charcoal)' }}>Employer Benefits</p>
            <h2 className="text-3xl font-bold" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
              Why employers choose Handlenders.
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {benefits?.map((b) => (
              <div
                key={b?.title}
                className={`rounded-2xl p-7 ${b?.span}`}
                style={{ background: b?.bg, color: b?.color }}
              >
                <h3 className="font-bold text-base lg:text-lg mb-2" style={{ fontFamily: 'var(--font-serif)' }}>{b?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ opacity: 0.8 }}>{b?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Process */}
      <section className="py-16 lg:py-20" style={{ background: 'var(--off-white)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>How it works</h2>
            <p className="text-muted-foreground">From search to hire in four straightforward steps.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {processSteps?.map((step, i) => (
              <div key={step?.title} className="rounded-2xl p-6 bg-white border" style={{ borderColor: 'var(--border)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--charcoal)' }}>
                  <step.icon size={18} color="white" />
                </div>
                <div className="text-xs font-bold text-muted-foreground mb-2">Step {i + 1}</div>
                <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--charcoal)' }}>{step?.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{step?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Pricing */}
      <section id="pricing" className="py-16 lg:py-24 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--charcoal)' }}>Transparent Pricing</p>
            <h2 className="text-3xl font-bold mb-3" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
              Pay per profile. No subscription. No risk.
            </h2>
            <p className="text-muted-foreground">Only pay when you find a candidate worth interviewing.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingCards?.map((card) => (
              <div
                key={card?.title}
                className="rounded-2xl p-7 flex flex-col"
                style={{
                  background: card?.highlight ? 'var(--charcoal)' : 'white',
                  border: card?.highlight ? 'none' : '1px solid var(--border)',
                  color: card?.highlight ? 'white' : 'var(--charcoal)',
                }}
              >
                <div className="text-xs font-bold uppercase tracking-wider mb-2 opacity-70">{card?.title}</div>
                <div className="text-3xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>{card?.price}</div>
                <p className="text-sm mb-5 leading-relaxed opacity-75">{card?.desc}</p>
                <ul className="flex flex-col gap-2 mb-6 flex-1">
                  {card?.features?.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle size={13} style={{ color: card?.highlight ? 'var(--amber)' : 'var(--charcoal)', flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  href={card?.href}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95"
                  style={{
                    background: card?.highlight ? 'var(--amber)' : 'var(--charcoal)',
                    color: card?.highlight ? 'var(--charcoal)' : 'white',
                  }}
                >
                  {card?.cta}
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Dashboard CTA */}
      <section className="py-12 lg:py-16" style={{ background: 'var(--charcoal)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-serif)' }}>Already registered?</h3>
            <p className="text-white/60 text-sm">Access your employer dashboard to manage saved candidates and purchased profiles.</p>
          </div>
          <Link
            href="/employer-dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95 flex-shrink-0"
            style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}
          >
            Access Dashboard →
          </Link>
        </div>
      </section>
      <HomeFooter />
    </div>
  );
}
